import { z } from "zod";
import {
  PASSWORD_REGEX,
  PASSWORD_REQUIREMENTS_KEY,
  isValidPhone,
} from "./validators";

// Messages below are translation KEYS, not display text — the schemas here
// are plain module-level objects (no React/hook context), so they can't call
// t() themselves. Every consumer (auth.validation.ts's parseSchema, and
// ChangePasswordCard.tsx which calls changePasswordSchema directly) is
// responsible for translating the returned message before showing it.

// ==================== Base Schemas ====================

/**
 * Login schema for user authentication
 */
export const loginSchema = z.object({
  email: z.string().min(1, "auth.validation.emailRequired").email("auth.validation.emailInvalid"),
  password: z
    .string()
    .min(1, "auth.validation.passwordRequired")
    .min(6, "auth.validation.loginPasswordMin"),
});

export type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Basic registration object schema - first step of signup (without refinement)
 */
const registerBasicObject = z.object({
  first_name: z
    .string()
    .min(1, "auth.validation.firstNameRequired")
    .min(2, "auth.validation.firstNameMin")
    .max(50, "auth.validation.firstNameMax")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "auth.validation.firstNamePattern",
    ),
  last_name: z
    .string()
    .min(1, "auth.validation.lastNameRequired")
    .min(2, "auth.validation.lastNameMin")
    .max(50, "auth.validation.lastNameMax")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "auth.validation.lastNamePattern",
    ),
  email: z.string().min(1, "auth.validation.emailRequired").email("auth.validation.emailInvalid"),
  phone: z
    .string()
    .min(1, "auth.validation.phoneRequired")
    .refine((val) => isValidPhone(val), "auth.validation.phoneInvalid"),
  phone_country: z.string().min(1, "auth.validation.countryRequired"),
  whatsapp_number: z
    .string()
    .optional()
    .refine((val) => !val || isValidPhone(val), "auth.validation.whatsappInvalid"),
  password: z
    .string()
    .min(1, "auth.validation.passwordRequired")
    .min(8, "auth.validation.passwordMin8")
    .regex(PASSWORD_REGEX, PASSWORD_REQUIREMENTS_KEY),
  confirmPassword: z.string().min(1, "auth.validation.confirmPasswordRequired"),
});

/**
 * Basic registration schema with password confirmation validation
 */
export const registerBasicSchema = registerBasicObject.refine(
  (data) => data.password === data.confirmPassword,
  {
    message: "auth.validation.passwordsMismatch",
    path: ["confirmPassword"],
  },
);

export type RegisterBasicFormData = z.infer<typeof registerBasicSchema>;

/**
 * Talent/Model profile extension schema
 */
export const talentProfileSchema = z.object({
  date_of_birth: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const dob = new Date(val);
      const today = new Date();
      const age =
        today.getFullYear() -
        dob.getFullYear() -
        (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate())
          ? 1
          : 0);
      return age >= 16;
    }, "You must be at least 16 years old"),
  nationality: z.string().optional(),
  city: z.string().optional(),
  bio: z.string().max(500, "Bio must not exceed 500 characters").optional(),
  specialties: z
    .array(z.string())
    .min(1, "Please select at least one specialty")
    .optional(),
  experience: z
    .enum(["beginner", "intermediate", "professional", "expert"])
    .optional(),
  portfolio: z
    .string()
    .url("Invalid portfolio URL")
    .optional()
    .or(z.literal("")),
  languages: z.array(z.string()).optional(),
  rates: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, "Invalid rate format")
    .optional()
    .or(z.literal("")),
  measurements: z
    .object({
      height: z
        .string()
        .regex(/^\d+(\.\d{2})?$/, "Invalid height")
        .optional()
        .or(z.literal("")),
      weight: z
        .string()
        .regex(/^\d+(\.\d{2})?$/, "Invalid weight")
        .optional()
        .or(z.literal("")),
      bust: z
        .string()
        .regex(/^\d+(\.\d{2})?$/, "Invalid measurement")
        .optional()
        .or(z.literal("")),
      waist: z
        .string()
        .regex(/^\d+(\.\d{2})?$/, "Invalid measurement")
        .optional()
        .or(z.literal("")),
      hips: z
        .string()
        .regex(/^\d+(\.\d{2})?$/, "Invalid measurement")
        .optional()
        .or(z.literal("")),
      shoeSize: z.string().optional().or(z.literal("")),
      dressSize: z.string().optional().or(z.literal("")),
    })
    .optional(),
  socialMedia: z
    .object({
      instagram: z
        .string()
        .url("Invalid Instagram URL")
        .optional()
        .or(z.literal("")),
      facebook: z
        .string()
        .url("Invalid Facebook URL")
        .optional()
        .or(z.literal("")),
      tiktok: z.string().url("Invalid TikTok URL").optional().or(z.literal("")),
    })
    .optional(),
});

export type TalentProfileFormData = z.infer<typeof talentProfileSchema>;

/**
 * Recruiter/Company profile extension schema
 */
export const recruiterProfileSchema = z.object({
  companyName: z
    .string()
    .min(1, "Company name is required")
    .max(100, "Company name must not exceed 100 characters"),
  city: z.string().min(1, "City is required"),
  bio: z.string().max(500, "Bio must not exceed 500 characters").optional(),
  website: z.string().url("Invalid website URL").optional().or(z.literal("")),
  socialMedia: z
    .object({
      instagram: z
        .string()
        .url("Invalid Instagram URL")
        .optional()
        .or(z.literal("")),
      facebook: z
        .string()
        .url("Invalid Facebook URL")
        .optional()
        .or(z.literal("")),
      twitter: z
        .string()
        .url("Invalid Twitter URL")
        .optional()
        .or(z.literal("")),
    })
    .optional(),
});

export type RecruiterProfileFormData = z.infer<typeof recruiterProfileSchema>;

/**
 * Complete registration schema (basic + role-specific)
 */
export const registerTalentSchema = registerBasicObject
  .merge(talentProfileSchema)
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export const registerRecruiterSchema = registerBasicObject
  .merge(recruiterProfileSchema)
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type RegisterTalentFormData = z.infer<typeof registerTalentSchema>;
export type RegisterRecruiterFormData = z.infer<typeof registerRecruiterSchema>;

/**
 * Social login schema
 */
export const socialLoginSchema = z.object({
  provider: z.enum(["google", "facebook", "github"]),
  token: z.string().min(1, "Provider token is required"),
});

export type SocialLoginFormData = z.infer<typeof socialLoginSchema>;

/**
 * Password reset request schema
 */
export const passwordResetRequestSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

export type PasswordResetRequestFormData = z.infer<
  typeof passwordResetRequestSchema
>;

/**
 * Password reset confirmation schema
 */
export const passwordResetSchema = z
  .object({
    token: z.string().min(1, "Reset token is required"),
    password: z
      .string()
      .min(1, "Password is required")
      .min(8, "Password must be at least 8 characters")
      .regex(PASSWORD_REGEX, PASSWORD_REQUIREMENTS_KEY),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

/**
 * Change password schema — used by logged-in users to change their own password
 */
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, "auth.validation.currentPasswordRequired"),
    password: z
      .string()
      .min(1, "auth.validation.newPasswordRequired")
      .min(8, "auth.validation.passwordMin8")
      .regex(PASSWORD_REGEX, PASSWORD_REQUIREMENTS_KEY),
    confirmPassword: z.string().min(1, "auth.validation.confirmNewPasswordRequired"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "auth.validation.passwordsMismatch",
    path: ["confirmPassword"],
  })
  .refine((data) => data.password !== data.current_password, {
    message: "auth.validation.newPasswordSameAsCurrent",
    path: ["password"],
  });

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

/**
 * Profile update schema
 */
export const profileUpdateSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "Name can only contain letters, spaces, hyphens, and apostrophes",
    )
    .optional(),
  phone: z
    .string()
    .refine((val) => isValidPhone(val), "Invalid phone number format")
    .optional()
    .or(z.literal("")),
  bio: z
    .string()
    .max(500, "Bio must not exceed 500 characters")
    .optional()
    .or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
});

export type ProfileUpdateFormData = z.infer<typeof profileUpdateSchema>;

// ==================== Validation Functions ====================

/**
 * Utility function to validate and get errors
 */
export const validateAuthForm = (
  schema: z.ZodSchema,
  data: any,
): { success: boolean; errors: Record<string, string> } => {
  try {
    schema.parse(data);
    return { success: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.issues.forEach((err) => {
        const path = err.path.join(".");
        errors[path] = err.message;
      });
      return { success: false, errors };
    }
    return { success: false, errors: { general: "Validation failed" } };
  }
};
